import type {
  CreateManagedReservationRequestDto,
  CreateManagedReservationResponseDto,
  CreateWaitlistEntryRequestDto,
  CreateWaitlistEntryResponseDto,
  PromoteWaitlistEntryRequestDto,
  PromoteWaitlistEntryResponseDto,
  RecordReservationDepositRequestDto,
  RecordReservationDepositResponseDto,
  SendReservationConfirmationRequestDto,
  SendReservationConfirmationResponseDto,
} from "./contracts/dtos.ts";
import {
  validateCreateManagedReservationRequest,
  validateCreateWaitlistEntryRequest,
  validateRecordReservationDepositRequest,
  validateSendReservationConfirmationRequest,
} from "./contracts/schemas.ts";
import type { ReservationConfirmationEmailProvider } from "./email.ts";
import type {
  ReservationAlert,
  ReservationAuditEntry,
  ReservationDeposit,
  ReservationManagementContext,
  ReservationOverbookingDecision,
  ReservationRecord,
  ReservationWaitlistEntry,
} from "./types.ts";

type IdFactory = (prefix: string) => string;

function overlaps(arrivalA: string, departureA: string, arrivalB: string, departureB: string) {
  return arrivalA <= departureB && departureA >= arrivalB;
}

function dateMinusDays(value: string, days: number) {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function nightsBetween(arrival: string, departure: string) {
  const start = new Date(`${arrival}T00:00:00`);
  const end = new Date(`${departure}T00:00:00`);
  const diff = Math.max(0, end.getTime() - start.getTime());
  return Math.max(1, Math.round(diff / 86400000));
}

function buildAuditEntry(
  idFactory: IdFactory,
  now: string,
  input: Omit<ReservationAuditEntry, "id" | "timestamp">
): ReservationAuditEntry {
  return {
    ...input,
    id: idFactory("audit"),
    timestamp: now,
  };
}

function buildAlert(
  idFactory: IdFactory,
  now: string,
  input: Omit<ReservationAlert, "id" | "createdAt">
): ReservationAlert {
  return {
    ...input,
    id: idFactory("alert"),
    createdAt: now,
  };
}

export function assessReservationInventory(
  roomTypeId: string,
  arrival: string,
  departure: string,
  rooms: number,
  context: ReservationManagementContext
) {
  const sellableRooms = context.inventoryByRoomType[roomTypeId] ?? 0;
  const reservedRooms = context.reservations.reduce((count, reservation) => {
    if (reservation.roomTypeId !== roomTypeId) return count;
    if (reservation.status === "cancelled" || reservation.status === "checkedout" || reservation.status === "noshow") return count;
    if (!overlaps(arrival, departure, reservation.arrival, reservation.departure)) return count;
    return count + reservation.rooms;
  }, 0);
  const remainingRooms = sellableRooms - reservedRooms;
  const overbookedRooms = Math.max(0, rooms - Math.max(0, remainingRooms));

  return {
    roomTypeId,
    sellableRooms,
    reservedRooms,
    remainingRooms,
    overbookedRooms,
  };
}

export function evaluateOverbookingDecision(
  snapshot: ReturnType<typeof assessReservationInventory>,
  allowOverride: boolean,
  context: ReservationManagementContext
): ReservationOverbookingDecision {
  if (snapshot.overbookedRooms <= 0) {
    return {
      allowed: true,
      mode: context.settings.mode,
      overbookedRooms: 0,
      remainingRooms: snapshot.remainingRooms - snapshot.overbookedRooms,
      reason: snapshot.remainingRooms <= context.settings.alertThreshold
        ? "Inventory is running low for this room type."
        : "Inventory is available for this reservation.",
    };
  }

  const allowed =
    context.settings.mode === "controlled" &&
    allowOverride &&
    snapshot.overbookedRooms <= context.settings.maxOverbookedRoomsPerRoomType;

  return {
    allowed,
    mode: context.settings.mode,
    overbookedRooms: snapshot.overbookedRooms,
    remainingRooms: snapshot.remainingRooms,
    reason: allowed
      ? `Controlled overbooking approved for ${snapshot.overbookedRooms} room(s).`
      : `Overbooking blocked for ${snapshot.overbookedRooms} room(s).`,
  };
}

export function buildDepositForReservation(
  request: CreateManagedReservationRequestDto,
  now: string
): ReservationDeposit {
  const policy = request.depositPolicy;
  const currency = request.pricingSnapshot.currency;
  const nights = nightsBetween(request.arrival, request.departure);
  const nightlyRate = request.pricingSnapshot.nightlyRate || request.pricingSnapshot.totalAmount / nights;

  let amount = 0;
  if (policy.type === "first_night") amount = Number(nightlyRate.toFixed(2));
  if (policy.type === "percentage") amount = Number((request.pricingSnapshot.totalAmount * ((policy.percentage ?? 0) / 100)).toFixed(2));
  if (policy.type === "fixed") amount = Number((policy.fixedAmount ?? 0).toFixed(2));

  if (policy.type === "none" || amount <= 0) {
    return {
      required: false,
      amount: 0,
      currency,
      status: "not_required",
      paidAmount: 0,
      outstandingAmount: 0,
      policy,
      history: [],
    };
  }

  return {
    required: true,
    amount,
    currency,
    dueAt: policy.dueDaysBeforeArrival !== undefined ? dateMinusDays(request.arrival, policy.dueDaysBeforeArrival) : now.slice(0, 10),
    status: "pending",
    paidAmount: 0,
    outstandingAmount: amount,
    policy,
    history: [],
  };
}

export function createManagedReservation(
  request: CreateManagedReservationRequestDto,
  context: ReservationManagementContext,
  dependencies: { idFactory: IdFactory }
): CreateManagedReservationResponseDto {
  const fieldErrors = validateCreateManagedReservationRequest(request);
  if (Object.keys(fieldErrors).length > 0) {
    return {
      ok: false,
      code: "VALIDATION_ERROR",
      message: "Reservation request is invalid.",
      fieldErrors,
    };
  }

  const inventorySnapshot = assessReservationInventory(
    request.roomTypeId,
    request.arrival,
    request.departure,
    request.rooms,
    context
  );
  const overbookingDecision = evaluateOverbookingDecision(
    inventorySnapshot,
    request.allowOverbookingOverride,
    context
  );

  if (!overbookingDecision.allowed) {
    return {
      ok: false,
      code: "OVERBOOKING_BLOCKED",
      message: overbookingDecision.reason,
      overbookingDecision,
    };
  }

  const deposit = buildDepositForReservation(request, context.now);
  const confirmation = {
    status: request.sendConfirmationEmail ? "pending" : "not_requested",
    requestedAt: request.sendConfirmationEmail ? context.now : undefined,
    recipientEmail: request.guestEmail?.trim() || undefined,
  } as const;

  const reservation: ReservationRecord = {
    id: request.reservationId,
    guestId: request.guestId,
    arrival: request.arrival,
    departure: request.departure,
    roomTypeId: request.roomTypeId,
    rooms: request.rooms,
    status: request.sendConfirmationEmail ? "pending_confirmation" : "booked",
    createdAt: context.now,
    source: request.source,
    pricingSnapshot: request.pricingSnapshot,
    deposit,
    confirmation,
    overbookingDecision,
  };

  const alerts: ReservationAlert[] = [];
  if (overbookingDecision.overbookedRooms > 0) {
    alerts.push(
      buildAlert(dependencies.idFactory, context.now, {
        type: "overbooking",
        severity: "critical",
        reservationId: reservation.id,
        roomTypeId: reservation.roomTypeId,
        message: overbookingDecision.reason,
      })
    );
  } else if (inventorySnapshot.remainingRooms <= context.settings.alertThreshold) {
    alerts.push(
      buildAlert(dependencies.idFactory, context.now, {
        type: "availability_risk",
        severity: "warning",
        reservationId: reservation.id,
        roomTypeId: reservation.roomTypeId,
        message: overbookingDecision.reason,
      })
    );
  }

  const auditEntries: ReservationAuditEntry[] = [
    buildAuditEntry(dependencies.idFactory, context.now, {
      reservationId: reservation.id,
      action: "reservation_created",
      actor: request.actor,
      message: `Reservation ${reservation.id} created.`,
      metadata: {
        roomTypeId: reservation.roomTypeId,
        rooms: reservation.rooms.toString(),
      },
    }),
  ];

  if (overbookingDecision.overbookedRooms > 0) {
    auditEntries.push(
      buildAuditEntry(dependencies.idFactory, context.now, {
        reservationId: reservation.id,
        action: "overbooking_override",
        actor: request.actor,
        message: overbookingDecision.reason,
        metadata: {
          overbookedRooms: overbookingDecision.overbookedRooms.toString(),
        },
      })
    );
  }

  return {
    ok: true,
    reservation,
    deposit,
    confirmation: reservation.confirmation!,
    overbookingDecision,
    alerts,
    auditEntries,
  };
}

export function recordReservationDeposit(
  reservation: ReservationRecord,
  request: RecordReservationDepositRequestDto,
  now: string,
  dependencies: { idFactory: IdFactory }
): RecordReservationDepositResponseDto {
  const fieldErrors = validateRecordReservationDepositRequest(request);
  if (Object.keys(fieldErrors).length > 0) {
    return {
      ok: false,
      message: "Deposit input is invalid.",
      fieldErrors,
    };
  }

  if (!reservation.deposit) {
    return {
      ok: false,
      message: "Reservation has no deposit policy.",
    };
  }

  const nextHistory = [
    {
      id: dependencies.idFactory("dep"),
      amount: request.amount,
      currency: request.currency,
      status: request.status,
      method: request.method,
      timestamp: now,
      reference: request.reference,
      note: request.note,
      actor: request.actor,
    },
    ...reservation.deposit.history,
  ];

  const paidAmount =
    request.status === "captured"
      ? Number((reservation.deposit.paidAmount + request.amount).toFixed(2))
      : request.status === "refunded"
        ? Math.max(0, Number((reservation.deposit.paidAmount - request.amount).toFixed(2)))
        : reservation.deposit.paidAmount;
  const outstandingAmount = Math.max(0, Number((reservation.deposit.amount - paidAmount).toFixed(2)));

  const status =
    request.status === "failed"
      ? "failed"
      : request.status === "authorized"
        ? "authorized"
        : request.status === "released"
          ? "released"
          : request.status === "refunded"
            ? "refunded"
            : outstandingAmount === 0
              ? "captured"
              : "partially_captured";

  const deposit: ReservationDeposit = {
    ...reservation.deposit,
    currency: request.currency,
    status,
    paidAmount,
    outstandingAmount,
    history: nextHistory,
    lastError: request.status === "failed" ? request.note || "Deposit capture failed." : undefined,
  };

  const auditEntry = buildAuditEntry(dependencies.idFactory, now, {
    reservationId: reservation.id,
    action: request.status === "failed" ? "deposit_failed" : "deposit_recorded",
    actor: request.actor,
    message:
      request.status === "failed"
        ? `Deposit failed for reservation ${reservation.id}.`
        : `Deposit ${request.status} for reservation ${reservation.id}.`,
    metadata: {
      amount: request.amount.toFixed(2),
      currency: request.currency,
    },
  });

  const alerts =
    request.status === "failed"
      ? [
          buildAlert(dependencies.idFactory, now, {
            type: "deposit_failed",
            severity: "critical",
            reservationId: reservation.id,
            message: `Deposit processing failed for ${reservation.id}.`,
          }),
        ]
      : [];

  return {
    ok: true,
    deposit,
    auditEntry,
    alerts,
  };
}

export async function sendReservationConfirmation(
  reservation: ReservationRecord,
  request: SendReservationConfirmationRequestDto,
  provider: ReservationConfirmationEmailProvider,
  now: string,
  dependencies: { idFactory: IdFactory; guestLabel: string }
): Promise<SendReservationConfirmationResponseDto> {
  const fieldErrors = validateSendReservationConfirmationRequest(request);
  if (Object.keys(fieldErrors).length > 0) {
    const confirmation = {
      ...(reservation.confirmation ?? { status: "failed" as const }),
      status: "failed" as const,
      recipientEmail: request.recipientEmail,
      lastError: "Confirmation request is invalid.",
    };
    return {
      ok: false,
      message: "Confirmation request is invalid.",
      confirmation,
      auditEntry: buildAuditEntry(dependencies.idFactory, now, {
        reservationId: reservation.id,
        action: "confirmation_failed",
        actor: request.actor,
        message: "Confirmation request validation failed.",
      }),
    };
  }

  try {
    const delivery = await provider.sendReservationConfirmation({
      reservationId: reservation.id,
      guestLabel: dependencies.guestLabel,
      recipientEmail: request.recipientEmail,
      arrival: reservation.arrival,
      departure: reservation.departure,
      roomTypeId: reservation.roomTypeId,
      totalAmount: reservation.pricingSnapshot?.totalAmount ?? 0,
      currency: reservation.pricingSnapshot?.currency ?? "EUR",
    });
    const confirmation = {
      status: "sent" as const,
      requestedAt: reservation.confirmation?.requestedAt ?? now,
      sentAt: now,
      recipientEmail: request.recipientEmail,
      providerId: delivery.providerId,
      providerMessageId: delivery.messageId,
    };
    return {
      ok: true,
      confirmation,
      auditEntry: buildAuditEntry(dependencies.idFactory, now, {
        reservationId: reservation.id,
        action: "confirmation_sent",
        actor: request.actor,
        message: `Confirmation email sent for reservation ${reservation.id}.`,
        metadata: {
          recipientEmail: request.recipientEmail,
        },
      }),
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "Confirmation delivery failed.";
    const confirmation = {
      status: "failed" as const,
      requestedAt: reservation.confirmation?.requestedAt ?? now,
      recipientEmail: request.recipientEmail,
      lastError: message,
    };
    return {
      ok: false,
      message,
      confirmation,
      auditEntry: buildAuditEntry(dependencies.idFactory, now, {
        reservationId: reservation.id,
        action: "confirmation_failed",
        actor: request.actor,
        message: `Confirmation email failed for reservation ${reservation.id}.`,
        metadata: {
          recipientEmail: request.recipientEmail,
        },
      }),
    };
  }
}

export function createWaitlistEntry(
  request: CreateWaitlistEntryRequestDto,
  now: string,
  dependencies: { idFactory: IdFactory }
): CreateWaitlistEntryResponseDto {
  const fieldErrors = validateCreateWaitlistEntryRequest(request);
  if (Object.keys(fieldErrors).length > 0) {
    return {
      ok: false,
      message: "Waitlist request is invalid.",
      fieldErrors,
    };
  }

  const entry: ReservationWaitlistEntry = {
    id: request.waitlistEntryId,
    guestId: request.guestId,
    guestLabel: request.guestLabel,
    email: request.email,
    arrival: request.arrival,
    departure: request.departure,
    roomTypeId: request.roomTypeId,
    rooms: request.rooms,
    priority: request.priority,
    status: "active",
    createdAt: now,
    source: request.source,
    note: request.note,
  };

  return {
    ok: true,
    entry,
    auditEntry: buildAuditEntry(dependencies.idFactory, now, {
      waitlistEntryId: entry.id,
      action: "reservation_waitlisted",
      actor: request.actor,
      message: `Waitlist entry ${entry.id} created.`,
      metadata: {
        roomTypeId: entry.roomTypeId,
      },
    }),
    alert: buildAlert(dependencies.idFactory, now, {
      type: "waitlist_created",
      severity: "info",
      waitlistEntryId: entry.id,
      roomTypeId: entry.roomTypeId,
      message: `${entry.guestLabel} added to waitlist.`,
    }),
  };
}

export function promoteWaitlistEntry(
  request: PromoteWaitlistEntryRequestDto,
  context: ReservationManagementContext & { waitlistEntry: ReservationWaitlistEntry },
  dependencies: { idFactory: IdFactory }
): PromoteWaitlistEntryResponseDto {
  const result = createManagedReservation(
    {
      reservationId: request.reservationId,
      guestId: request.guestId,
      guestLabel: context.waitlistEntry.guestLabel,
      guestEmail: context.waitlistEntry.email,
      arrival: context.waitlistEntry.arrival,
      departure: context.waitlistEntry.departure,
      roomTypeId: context.waitlistEntry.roomTypeId,
      rooms: context.waitlistEntry.rooms,
      source: context.waitlistEntry.source,
      pricingSnapshot: request.pricingSnapshot,
      depositPolicy: request.depositPolicy,
      sendConfirmationEmail: request.sendConfirmationEmail,
      allowOverbookingOverride: false,
      actor: request.actor,
    },
    context,
    dependencies
  );

  if (!result.ok) {
    return {
      ok: false,
      message: result.message,
      fieldErrors: result.fieldErrors,
    };
  }

  const waitlistEntry: ReservationWaitlistEntry = {
    ...context.waitlistEntry,
    status: "promoted",
    promotedReservationId: result.reservation.id,
  };
  const promotionAudit = buildAuditEntry(dependencies.idFactory, context.now, {
    reservationId: result.reservation.id,
    waitlistEntryId: waitlistEntry.id,
    action: "waitlist_promoted",
    actor: request.actor,
    message: `Waitlist entry ${waitlistEntry.id} promoted to reservation ${result.reservation.id}.`,
  });
  const promotionAlert = buildAlert(dependencies.idFactory, context.now, {
    type: "waitlist_promoted",
    severity: "info",
    reservationId: result.reservation.id,
    waitlistEntryId: waitlistEntry.id,
    roomTypeId: waitlistEntry.roomTypeId,
    message: `${waitlistEntry.guestLabel} promoted from waitlist.`,
  });

  return {
    ok: true,
    reservation: result.reservation,
    waitlistEntry,
    alerts: [...result.alerts, promotionAlert],
    auditEntries: [...result.auditEntries, promotionAudit],
  };
}

